import './sentry'; // MUST be first — install error handlers before anything renders
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import '@ah/design/styles';
import '@ah/design/theme.css';
import './styles.css';
import { App } from './App';
import { captureSessionToken } from './accounts';

// Must run before React mounts so the very first `me` query uses the newly
// captured OAuth token — otherwise the fetch races the capture and "Add
// account" flows wipe the previously-active account.
captureSessionToken();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 30_000 },
  },
});

function FallbackError({ resetError }: { resetError: () => void }) {
  return (
    <div className="min-h-dvh flex flex-col items-center justify-center gap-4 bg-background p-6 text-center">
      <h1 className="text-xl font-semibold">Something went wrong</h1>
      <p className="text-sm opacity-70">We&apos;ve been notified. Try reloading.</p>
      <button
        className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
        onClick={() => { resetError(); window.location.reload(); }}
      >
        Reload
      </button>
    </div>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={FallbackError} showDialog={false}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <App />
        </BrowserRouter>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
);
